import { Navigate, useLocation } from 'react-router'
import { useAuth } from '@/providers/auth-provider'
import { LoadingState } from './loading-state'

interface ProtectedRouteProps {
  /** If specified, also checks user.type matches */
  requiredType?: 'admin' | 'client'
  children: React.ReactNode
}

/**
 * ProtectedRoute — wraps a route to require authentication.
 *
 * Behaviour:
 * - While auth is loading: shows full-page spinner (prevents flash of login)
 * - Not authenticated: redirects to /login?returnTo={currentPath}
 * - Wrong user type: redirects to the correct dashboard
 *
 * The returnTo param is consumed by the login page to redirect back after auth.
 */
export function ProtectedRoute({ requiredType, children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <LoadingState variant="page" />
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    )
  }

  // Type mismatch — redirect to the correct dashboard
  if (requiredType && user?.type !== requiredType) {
    const fallback = user?.type === 'admin' ? '/admin' : '/portal'
    return <Navigate to={fallback} replace />
  }

  return <>{children}</>
}
